import { useNavigation, useRoute } from '@react-navigation/native';
import { useLocalObservable } from 'mobx-react-lite';
import { useContext, useEffect } from 'react';

import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { useDebouncedCallback } from './useDebouncedCallback';
import type {
  SearchTabNavigatorNavigationPropType,
  SearchTabNavigatorRoutePropType,
} from '@navigation';
import { SearchTabNavigatorContext } from '@context';

type SearchOperationType = (
  query: string,
) => Promise<any> | undefined;

type NavigationPropType = SearchTabNavigatorNavigationPropType<
  'SearchProfile' | 'SearchTutorial'
>;

type RoutePropType = SearchTabNavigatorRoutePropType<
  'SearchProfile' | 'SearchTutorial'
>;

type PositionType = {
  start: number;
  length: number;
};

type ResultDataType = {
  id: number;
  matchesPosition: Record<string, PositionType[]>;
}[];

type MatchedPositionType = Record<
  number,
  {
    description?: PositionType[];
    title?: PositionType[];
  }
>;

export interface ISearchStore {
  matchesPosition: MatchedPositionType;
  setPositions(value: MatchedPositionType): void;
  getPositionById(id: number): MatchedPositionType[number];
}

export function useSearch() {
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const searchTabNavigator = useContext(SearchTabNavigatorContext);

  useEffect(() => {
    // Set query into navigation params to handle linking (search/tutorial?query=Test) cases
    searchTabNavigator.addChangeQueryListener((q) =>
      nav.setParams({ query: q }),
    );
  }, []);

  const store = useLocalObservable<ISearchStore>(() => ({
    matchesPosition: {},

    setPositions(value: MatchedPositionType) {
      this.matchesPosition = { ...this.matchesPosition, ...value };
    },

    getPositionById(id: number) {
      return this.matchesPosition?.[id];
    },
  }));

  const query = route.params?.query?.trim();

  function getRecordOfMatchedPosition(data: ResultDataType) {
    return data.reduce<MatchedPositionType>((acc, value) => {
      acc[value.id] = value.matchesPosition;

      return acc;
    }, {});
  }

  async function performFetch(operation: SearchOperationType) {
    if (!query) {
      return;
    }

    try {
      const data = await operation(query);

      if (data) {
        // TODO: data can be array in refresh case, need to have this
        if (data.videos) {
          store.setPositions(getRecordOfMatchedPosition(data.videos));
        }
        // Add check for users here
      }
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const updateSearchQuery = useDebouncedCallback(
    (text: string) => {
      nav.setParams({ query: text });
    },
    [],
    400,
  );

  return { performFetch, updateSearchQuery, query, store };
}
